export const GA_MEASUREMENT_ID = 'G-B0PCHJ3ETB';
export const ANALYTICS_CONSENT_COOKIE = 'unity_cookie_consent';
export const ANALYTICS_CONSENT_STORAGE_KEY = 'unity_cookie_consent_v2';

const LEGACY_ANALYTICS_CONSENT_KEY = 'unity_cookie_analytics';
const CONSENT_VERSION = 'v2';
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;
const VALID_CONSENT = new Set(['granted', 'denied']);

const canUseBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';
let memoryConsent = null;

function normaliseConsent(value) {
  return VALID_CONSENT.has(value) ? value : null;
}

function serialiseConsent(value) {
  return `${CONSENT_VERSION}:${value}`;
}

function parseConsent(value) {
  if (!value) return null;
  const [version, status] = String(value).split(':');
  if (version !== CONSENT_VERSION) return null;
  return normaliseConsent(status);
}

function readCookie(name) {
  if (!canUseBrowser()) return null;
  const prefix = `${name}=`;
  const entry = document.cookie
    .split(';')
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix));
  if (!entry) return null;
  try {
    return decodeURIComponent(entry.slice(prefix.length));
  } catch {
    return entry.slice(prefix.length);
  }
}

function writeConsentCookie(value) {
  if (!canUseBrowser()) return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${ANALYTICS_CONSENT_COOKIE}=${encodeURIComponent(serialiseConsent(value))}; Max-Age=${CONSENT_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

function readStorage(key) {
  if (!canUseBrowser()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  if (!canUseBrowser()) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Browsers can deny localStorage. The first-party consent cookie remains the primary record.
  }
}

function removeStorage(key) {
  if (!canUseBrowser()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing else to do.
  }
}

export function getAnalyticsConsent() {
  if (!canUseBrowser()) return null;

  const cookieConsent = parseConsent(readCookie(ANALYTICS_CONSENT_COOKIE));
  if (cookieConsent) {
    memoryConsent = cookieConsent;
    return cookieConsent;
  }

  const storedConsent = parseConsent(readStorage(ANALYTICS_CONSENT_STORAGE_KEY));
  if (storedConsent) {
    memoryConsent = storedConsent;
    writeConsentCookie(storedConsent);
    return storedConsent;
  }

  const legacyConsent = normaliseConsent(readStorage(LEGACY_ANALYTICS_CONSENT_KEY));
  if (legacyConsent) {
    setAnalyticsConsent(legacyConsent);
    return legacyConsent;
  }

  return memoryConsent;
}

export function setAnalyticsConsent(value) {
  const consent = normaliseConsent(value);
  if (!consent) return;
  memoryConsent = consent;
  if (!canUseBrowser()) return;

  writeConsentCookie(consent);
  writeStorage(ANALYTICS_CONSENT_STORAGE_KEY, serialiseConsent(consent));
  removeStorage(LEGACY_ANALYTICS_CONSENT_KEY);
}

function ensureGtagQueue() {
  if (!canUseBrowser()) return null;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
  return window.gtag;
}

function configureConsentDefaults() {
  const gtag = ensureGtagQueue();
  if (!gtag || window.__unityConsentDefaultsSet) return gtag;

  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });
  window.__unityConsentDefaultsSet = true;
  return gtag;
}

function injectGoogleTag() {
  if (!canUseBrowser() || document.getElementById('unity-google-tag')) return;
  const script = document.createElement('script');
  script.id = 'unity-google-tag';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

export function initializeGoogleConsent() {
  if (!canUseBrowser()) return;

  configureConsentDefaults();

  // Keep analytics disabled until the visitor has explicitly consented. If a
  // previous consent exists, restore the corresponding state before React
  // mounts so no page-view can be sent in the interim.
  if (getAnalyticsConsent() === 'granted') {
    enableGoogleAnalytics();
  } else {
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  }
}

export function enableGoogleAnalytics() {
  if (!canUseBrowser()) return;
  setAnalyticsConsent('granted');
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;

  const gtag = configureConsentDefaults();
  if (!gtag) return;

  gtag('consent', 'update', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });

  if (!window.__unityGaInitialized) {
    injectGoogleTag();
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
    window.__unityGaInitialized = true;
  }

  window.dispatchEvent(new CustomEvent('unity:analytics-ready'));
}

function analyticsCookieDomains() {
  if (!canUseBrowser()) return [''];
  const hostname = window.location.hostname;
  const domains = new Set(['', hostname, `.${hostname}`]);
  const parts = hostname.split('.').filter(Boolean);
  for (let index = 1; index < parts.length - 1; index += 1) {
    domains.add(`.${parts.slice(index).join('.')}`);
  }
  return Array.from(domains);
}

function expireAnalyticsCookies() {
  if (!canUseBrowser()) return;
  const names = document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('=')[0])
    .filter((name) => name === '_ga' || name.startsWith('_ga_'));

  names.forEach((name) => {
    analyticsCookieDomains().forEach((domain) => {
      const domainPart = domain ? `; Domain=${domain}` : '';
      document.cookie = `${name}=; Max-Age=0; Path=/${domainPart}; SameSite=Lax`;
    });
  });
}

export function disableGoogleAnalytics() {
  if (!canUseBrowser()) return;
  setAnalyticsConsent('denied');
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  expireAnalyticsCookies();

  if (window.gtag || window.__unityGaInitialized) {
    const gtag = ensureGtagQueue();
    gtag('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }
}

export function trackGooglePageView(path) {
  if (!canUseBrowser()) return;
  if (getAnalyticsConsent() !== 'granted' || !window.__unityGaInitialized || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_path: path,
  });
}
